import React from 'react'
import { Box, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material'

import { type AIPhoneLineTypeEnum, type ELVoice, type PhonicVoice } from '~types'

import VoiceConfigElevenLabs from '../VoiceConfigElevenLabs'
import VoiceConfigPhonic from '../VoiceConfigPhonic'

import { getPhoneLineTypeDisplay } from './utils'
import { type ElevenLabsVoiceSettings, type PhonicVoiceSettings } from './voiceSettings'

export interface VoiceSettingsStepProps {
  phoneLineType: AIPhoneLineTypeEnum
  onPhoneLineTypeChange: (type: AIPhoneLineTypeEnum) => void
  canChangePhoneLineType: boolean
  greetingMessage: string
  elevenLabsVoices: ELVoice[]
  elevenLabsSettings: ElevenLabsVoiceSettings
  onElevenLabsSettingsChange: (settings: Partial<ElevenLabsVoiceSettings>) => void
  isPreviewLoading: boolean
  isPreviewPlaying: boolean
  onPreviewGreeting: () => void
  phonicVoices: PhonicVoice[]
  phonicSettings: PhonicVoiceSettings
  onPhonicSettingsChange: (settings: Partial<PhonicVoiceSettings>) => void
}

const VoiceSettingsStep: React.FC<VoiceSettingsStepProps> = ({
  phoneLineType,
  onPhoneLineTypeChange,
  canChangePhoneLineType,
  greetingMessage,
  elevenLabsVoices,
  elevenLabsSettings,
  onElevenLabsSettingsChange,
  isPreviewLoading,
  isPreviewPlaying,
  onPreviewGreeting,
  phonicVoices,
  phonicSettings,
  onPhonicSettingsChange,
}) => (
  <Stack spacing={2}>
    <Box className='phone-line-dialog__step-header'>
      {canChangePhoneLineType && (
        <ToggleButtonGroup
          size='small'
          exclusive
          value={phoneLineType}
          onChange={(_, value) => {
            if (value === null) return
            onPhoneLineTypeChange(value as AIPhoneLineTypeEnum)
          }}
          aria-label='Voice model'
        >
          <ToggleButton value='ELEVEN_LABS' aria-label='Studio voice'>
            {getPhoneLineTypeDisplay('ELEVEN_LABS')}
          </ToggleButton>
          <ToggleButton value='PHONIC' aria-label='Conversational voice'>
            {getPhoneLineTypeDisplay('PHONIC')}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
    </Box>

    {phoneLineType === 'ELEVEN_LABS' ? (
      <VoiceConfigElevenLabs
        voiceId={elevenLabsSettings.voiceId}
        onVoiceChange={(voiceId) => onElevenLabsSettingsChange({ voiceId })}
        voices={elevenLabsVoices}
        voiceSpeed={elevenLabsSettings.speed}
        onVoiceSpeedChange={(speed) => onElevenLabsSettingsChange({ speed })}
        voiceStability={elevenLabsSettings.stability}
        onVoiceStabilityChange={(stability) => onElevenLabsSettingsChange({ stability })}
        voiceSimilarity={elevenLabsSettings.similarity}
        onVoiceSimilarityChange={(similarity) => onElevenLabsSettingsChange({ similarity })}
        isPreviewLoading={isPreviewLoading}
        isPreviewPlaying={isPreviewPlaying}
        onPreviewGreeting={onPreviewGreeting}
        greetingMessage={greetingMessage}
      />
    ) : (
      <VoiceConfigPhonic
        voiceId={phonicSettings.voiceId}
        onVoiceChange={(voiceId) => onPhonicSettingsChange({ voiceId })}
        voices={phonicVoices}
        audioSpeed={phonicSettings.audioSpeed}
        onAudioSpeedChange={(audioSpeed) => onPhonicSettingsChange({ audioSpeed })}
        isWelcomeMessageInterruptible={phonicSettings.isWelcomeMessageInterruptible}
        onIsWelcomeMessageInterruptibleChange={(isWelcomeMessageInterruptible) =>
          onPhonicSettingsChange({ isWelcomeMessageInterruptible })
        }
        minWordsToInterrupt={phonicSettings.minWordsToInterrupt}
        onMinWordsToInterruptChange={(minWordsToInterrupt) =>
          onPhonicSettingsChange({ minWordsToInterrupt })
        }
        enableAssistantBackchannel={phonicSettings.enableAssistantBackchannel}
        onEnableAssistantBackchannelChange={(enableAssistantBackchannel) =>
          onPhonicSettingsChange({ enableAssistantBackchannel })
        }
        assistantBackchannelAggressiveness={phonicSettings.assistantBackchannelAggressiveness}
        onAssistantBackchannelAggressivenessChange={(assistantBackchannelAggressiveness) =>
          onPhonicSettingsChange({ assistantBackchannelAggressiveness })
        }
        backgroundNoise={phonicSettings.backgroundNoise}
        onBackgroundNoiseChange={(backgroundNoise) =>
          onPhonicSettingsChange({
            backgroundNoise,
            // Phonic rejects a non-zero level when noise type is null
            ...(backgroundNoise === null && { backgroundNoiseLevel: 0 }),
          })
        }
        backgroundNoiseLevel={phonicSettings.backgroundNoiseLevel}
        onBackgroundNoiseLevelChange={(backgroundNoiseLevel) =>
          onPhonicSettingsChange({ backgroundNoiseLevel })
        }
        boostedKeywords={phonicSettings.boostedKeywords}
        onBoostedKeywordsChange={(boostedKeywords) => onPhonicSettingsChange({ boostedKeywords })}
        pronunciationDictionary={phonicSettings.pronunciationDictionary}
        onPronunciationDictionaryChange={(pronunciationDictionary) =>
          onPhonicSettingsChange({ pronunciationDictionary })
        }
      />
    )}
  </Stack>
)

export default VoiceSettingsStep
